/**
 * Notification Settings Page (Admin > Settings > Notifications)
 *
 * Org-wide notification controls. Every toggle here layers ON TOP of each
 * crew member's personal notification preferences: org-off means nobody
 * gets that notice, org-on means personal preferences decide.
 *
 * The SMS section is tier-gated (Enterprise only, season pass counts) —
 * the server computes `smsTierAllowed`, and when the tier doesn't include
 * SMS the switch renders locked with an upgrade pointer.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useOrganization } from '../../../context/OrganizationContext';
import { useAuth } from '../../../context/AuthContext';
import {
  getOrganization,
  updateOrganization,
  sendAdminDigest,
  Organization,
  UpdateOrganizationInput,
  DigestWeekday,
  PendingNudgeSlot,
  DigestExternalRecipient,
  AdminDigestSendMode,
} from '../../../api/organizations';
import type { EventReminder, ReminderTiming } from '../../../api/auth';
import { getErrorMessage } from '../../../api/client';
import { ToggleRow, TierLockedToggleRow } from '../../../components/ui/ToggleRow';
import { ConfirmDialog } from '../../../components/ui';
import {
  ExternalRecipientsEditor,
  MAX_EXTERNAL_RECIPIENTS,
} from '../../../components/settings/ExternalRecipientsEditor';

const MAX_DEFAULT_REMINDERS = 2;

// Same labels as the personal EventReminderSettings card — the row ends
// with a static "before crew call".
const TIMING_OPTIONS: { value: ReminderTiming; label: string }[] = [
  { value: 'WEEK_BEFORE', label: '1 week' },
  { value: 'DAY_BEFORE', label: '1 day' },
  { value: 'NIGHT_BEFORE', label: 'The evening (8 PM)' },
  { value: 'TWO_HOURS_BEFORE', label: '2 hours' },
];

// Email/push only: a brand-new crew member can't have completed the SMS
// opt-in yet, so a seeded text reminder would never fire.
const METHOD_OPTIONS: { value: 'email' | 'push'; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'push', label: 'Push' },
];

// Up to two scheduled send moments for the unanswered-work reminders.
const MAX_NUDGE_SLOTS = 2;
// Up to two daily send hours for the admins digest (server: MAX_ADMIN_DIGEST_HOURS).
const MAX_ADMIN_DIGEST_HOURS = 2;
// Starts at 2, not 1. One missed event is a bad week, not a departure, and an
// org that archived on a single miss would spend its season restoring people.
const AUTO_ARCHIVE_EVENT_OPTIONS = [2, 3, 4, 5, 6, 8, 10, 15, 20];

// Send-day options for the weekly digests. Values are the backend's weekday
// tokens (what the sweep compares against org-local time).
const WEEKDAY_OPTIONS: { value: DigestWeekday; label: string }[] = [
  { value: 'Sun', label: 'Sunday' },
  { value: 'Mon', label: 'Monday' },
  { value: 'Tue', label: 'Tuesday' },
  { value: 'Wed', label: 'Wednesday' },
  { value: 'Thu', label: 'Thursday' },
  { value: 'Fri', label: 'Friday' },
  { value: 'Sat', label: 'Saturday' },
];

// The digest sweep runs hourly, so sends land on the hour.
const HOUR_OPTIONS: { value: number; label: string }[] = Array.from(
  { length: 24 },
  (_, hour) => ({
    value: hour,
    label: `${hour % 12 === 0 ? 12 : hour % 12}:00 ${hour < 12 ? 'AM' : 'PM'}`,
  })
);

type NotificationFormData = Required<
  Pick<
    UpdateOrganizationInput,
    | 'notifyOnEventPublished'
    | 'notifyOnEventChanges'
    | 'notifyAdminsOnDecline'
    | 'smsNotificationsEnabled'
    | 'pendingNudgeEnabled'
    | 'adminDigestEnabled'
    | 'weeklyDigestEnabled'
    | 'weeklyDigestDay'
    | 'weeklyDigestHour'
    | 'orgWeeklyDigestEnabled'
    | 'orgWeeklyDigestDay'
    | 'orgWeeklyDigestHour'
    | 'autoArchiveEnabled'
    | 'autoArchiveAfterEvents'
  >
> & {
  defaultCrewReminders: EventReminder[];
  digestExternalRecipients: DigestExternalRecipient[];
  pendingNudgeSlots: PendingNudgeSlot[];
  adminDigestHours: number[];
  adminDigestExternalRecipients: DigestExternalRecipient[];
};

const DEFAULT_FORM: NotificationFormData = {
  notifyOnEventPublished: true,
  notifyOnEventChanges: true,
  notifyAdminsOnDecline: true,
  smsNotificationsEnabled: true,
  pendingNudgeEnabled: false,
  adminDigestEnabled: true,
  weeklyDigestEnabled: false,
  weeklyDigestDay: 'Mon',
  weeklyDigestHour: 8,
  orgWeeklyDigestEnabled: false,
  orgWeeklyDigestDay: 'Mon',
  orgWeeklyDigestHour: 8,
  autoArchiveEnabled: false,
  autoArchiveAfterEvents: 3,
  defaultCrewReminders: [],
  digestExternalRecipients: [],
  pendingNudgeSlots: [],
  adminDigestHours: [8],
  adminDigestExternalRecipients: [],
};

function sanitizeExternalRecipients(raw: unknown): DigestExternalRecipient[] {
  if (!Array.isArray(raw)) return [];
  return (raw as DigestExternalRecipient[])
    .filter((r) => r && typeof r.name === 'string' && typeof r.email === 'string')
    .slice(0, MAX_EXTERNAL_RECIPIENTS);
}

function sanitizeNudgeSlots(raw: unknown): PendingNudgeSlot[] {
  if (!Array.isArray(raw)) return [];
  return (raw as PendingNudgeSlot[])
    .filter(
      (slot) =>
        slot &&
        WEEKDAY_OPTIONS.some((d) => d.value === slot.day) &&
        Number.isInteger(slot.hour) &&
        slot.hour >= 0 &&
        slot.hour <= 23
    )
    .slice(0, MAX_NUDGE_SLOTS);
}

// Integers 0-23, deduped, sorted, capped: the same normalisation the server
// applies on save, so what the page shows is what is stored.
function sanitizeAdminDigestHours(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const hours = new Set<number>();
  for (const value of raw) {
    if (Number.isInteger(value) && value >= 0 && value <= 23) hours.add(value as number);
  }
  return [...hours].sort((a, b) => a - b).slice(0, MAX_ADMIN_DIGEST_HOURS);
}

function sanitizeReminders(raw: unknown): EventReminder[] {
  if (!Array.isArray(raw)) return [];
  return (raw as EventReminder[])
    .filter(
      (r) =>
        r &&
        TIMING_OPTIONS.some((t) => t.value === r.timing) &&
        METHOD_OPTIONS.some((m) => m.value === r.method)
    )
    .slice(0, MAX_DEFAULT_REMINDERS);
}

export default function NotificationSettings() {
  const { currentOrg, isOrgOwner, isOrgAdmin } = useOrganization();
  const { refreshUser } = useAuth();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<NotificationFormData>(DEFAULT_FORM);
  // What the server holds, for the admins-digest send buttons: they use the
  // SAVED settings, so they sit disabled while the form has unsaved edits.
  const [savedForm, setSavedForm] = useState<NotificationFormData>(DEFAULT_FORM);

  const fetchOrganization = useCallback(async () => {
    try {
      const response = await getOrganization();
      const org = response.organization;
      setOrganization(org);
      const loaded: NotificationFormData = {
        notifyOnEventPublished: org.notifyOnEventPublished,
        notifyOnEventChanges: org.notifyOnEventChanges,
        notifyAdminsOnDecline: org.notifyAdminsOnDecline,
        smsNotificationsEnabled: org.smsNotificationsEnabled,
        pendingNudgeEnabled: org.pendingNudgeEnabled ?? false,
        adminDigestEnabled: org.adminDigestEnabled ?? true,
        weeklyDigestEnabled: org.weeklyDigestEnabled,
        weeklyDigestDay: org.weeklyDigestDay ?? 'Mon',
        weeklyDigestHour: org.weeklyDigestHour ?? 8,
        orgWeeklyDigestEnabled: org.orgWeeklyDigestEnabled ?? false,
        orgWeeklyDigestDay: org.orgWeeklyDigestDay ?? 'Mon',
        orgWeeklyDigestHour: org.orgWeeklyDigestHour ?? 8,
        autoArchiveEnabled: org.autoArchiveEnabled ?? false,
        autoArchiveAfterEvents: org.autoArchiveAfterEvents ?? 3,
        defaultCrewReminders: sanitizeReminders(org.defaultCrewReminders),
        digestExternalRecipients: sanitizeExternalRecipients(org.digestExternalRecipients),
        pendingNudgeSlots: sanitizeNudgeSlots(org.pendingNudgeSlots),
        adminDigestHours: sanitizeAdminDigestHours(org.adminDigestHours),
        adminDigestExternalRecipients: sanitizeExternalRecipients(
          org.adminDigestExternalRecipients
        ),
      };
      setFormData(loaded);
      setSavedForm(loaded);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchOrganization();
  }, [fetchOrganization]);

  const smsTierAllowed = organization?.smsTierAllowed ?? false;

  const setField = <K extends keyof NotificationFormData>(
    field: K,
    value: NotificationFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateReminderRow = (index: number, patch: Partial<EventReminder>) => {
    setField(
      'defaultCrewReminders',
      formData.defaultCrewReminders.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  const addReminderRow = () => {
    if (formData.defaultCrewReminders.length >= MAX_DEFAULT_REMINDERS) return;
    const next: EventReminder =
      formData.defaultCrewReminders.length === 0
        ? { method: 'push', timing: 'TWO_HOURS_BEFORE' }
        : { method: 'email', timing: 'DAY_BEFORE' };
    setField('defaultCrewReminders', [...formData.defaultCrewReminders, next]);
  };

  const removeReminderRow = (index: number) => {
    setField(
      'defaultCrewReminders',
      formData.defaultCrewReminders.filter((_, i) => i !== index)
    );
  };

  const updateNudgeSlot = (index: number, patch: Partial<PendingNudgeSlot>) => {
    setField(
      'pendingNudgeSlots',
      formData.pendingNudgeSlots.map((slot, i) => (i === index ? { ...slot, ...patch } : slot))
    );
  };

  const addNudgeSlot = () => {
    if (formData.pendingNudgeSlots.length >= MAX_NUDGE_SLOTS) return;
    // Sunday evening catches the week ahead; Wednesday morning is the last
    // useful moment to chase a weekend that is still short.
    const next: PendingNudgeSlot =
      formData.pendingNudgeSlots.length === 0
        ? { day: 'Sun', hour: 18 }
        : { day: 'Wed', hour: 9 };
    setField('pendingNudgeSlots', [...formData.pendingNudgeSlots, next]);
  };

  const removeNudgeSlot = (index: number) => {
    setField(
      'pendingNudgeSlots',
      formData.pendingNudgeSlots.filter((_, i) => i !== index)
    );
  };

  // ---- Admins digest send hours ----
  const updateAdminDigestHour = (index: number, hour: number) => {
    setField(
      'adminDigestHours',
      formData.adminDigestHours.map((h, i) => (i === index ? hour : h))
    );
  };

  const addAdminDigestHour = () => {
    if (formData.adminDigestHours.length >= MAX_ADMIN_DIGEST_HOURS) return;
    // A morning brief first; the second slot is the end-of-day wrap-up.
    const next = formData.adminDigestHours.includes(8) ? 18 : 8;
    setField('adminDigestHours', [...formData.adminDigestHours, next]);
  };

  const removeAdminDigestHour = (index: number) => {
    setField(
      'adminDigestHours',
      formData.adminDigestHours.filter((_, i) => i !== index)
    );
  };

  // ---- Admins digest manual send (uses the SAVED settings) ----
  const isDirty = JSON.stringify(formData) !== JSON.stringify(savedForm);
  const [sendingMode, setSendingMode] = useState<AdminDigestSendMode | null>(null);
  const [confirmSendAllOpen, setConfirmSendAllOpen] = useState(false);

  const triggerAdminDigestSend = async (mode: AdminDigestSendMode) => {
    setSendingMode(mode);
    try {
      await sendAdminDigest(mode);
      toast.success(
        mode === 'preview'
          ? 'Preview queued. It will reach your inbox in a minute or two.'
          : 'Digest queued. It will reach everyone in a minute or two.'
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSendingMode(null);
      setConfirmSendAllOpen(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const response = await updateOrganization({
        ...formData,
        defaultCrewReminders:
          formData.defaultCrewReminders.length > 0 ? formData.defaultCrewReminders : null,
        digestExternalRecipients:
          formData.digestExternalRecipients.length > 0
            ? formData.digestExternalRecipients
            : null,
        pendingNudgeSlots:
          formData.pendingNudgeSlots.length > 0 ? formData.pendingNudgeSlots : null,
        adminDigestHours: sanitizeAdminDigestHours(formData.adminDigestHours),
        adminDigestExternalRecipients:
          formData.adminDigestExternalRecipients.length > 0
            ? formData.adminDigestExternalRecipients
            : null,
      });
      setOrganization((prev) => ({ ...prev, ...response.organization }));
      // The server normalises the hours (and seeds [8] when the digest was
      // switched on with none); reflect what it stored.
      const saved: NotificationFormData = {
        ...formData,
        adminDigestHours: sanitizeAdminDigestHours(
          response.organization.adminDigestHours ?? formData.adminDigestHours
        ),
      };
      setFormData(saved);
      setSavedForm(saved);
      // Refresh org context so smsEnabled reflects the kill switch.
      await refreshUser();
      toast.success('Notification settings saved');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
        <p className="mt-2 text-gray-600">Loading notification settings...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-4">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/admin/settings" className="hover:text-gray-700">
              Settings
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="w-4 h-4" />
          </li>
          <li className="text-gray-900 font-medium">Notifications</li>
        </ol>
      </nav>

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
        <p className="text-gray-600 mt-1">
          Organization-wide notification controls for {currentOrg?.orgName || 'your organization'}.
          Crew members' personal preferences still apply to anything enabled here.
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Owner-only notice */}
      {!isOrgOwner && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex">
            <InfoIcon className="w-5 h-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-yellow-700">
              <p className="font-medium">View Only</p>
              <p className="mt-1">Only organization owners can modify these settings.</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Event notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Event Notifications</h2>

          <ToggleRow
            title="Announce published events"
            description="Notify all crew when a new event is published, so they can flag their availability. Crew who opted in to new-event notices receive it."
            enabled={formData.notifyOnEventPublished}
            onChange={(v) => setField('notifyOnEventPublished', v)}
            disabled={!isOrgOwner}
          />
          <ToggleRow
            title="Notify crew of event changes"
            description="Notify assigned crew when an event's date, time, or venue changes, or when it's cancelled."
            enabled={formData.notifyOnEventChanges}
            onChange={(v) => setField('notifyOnEventChanges', v)}
            disabled={!isOrgOwner}
          />
          <ToggleRow
            title="Notify admins when crew declines"
            description="Email admins when a crew member declines an assignment."
            enabled={formData.notifyAdminsOnDecline}
            onChange={(v) => setField('notifyAdminsOnDecline', v)}
            disabled={!isOrgOwner}
            last
          />
        </div>

        {/* Text messages */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Text Messages</h2>

          {smsTierAllowed ? (
            <ToggleRow
              title="Send text notifications"
              description="Allow this organization to send SMS for broadcasts, event notices, assignment notices, and reminders. Individual crew members still need the verified text opt-in."
              enabled={formData.smsNotificationsEnabled}
              onChange={(v) => setField('smsNotificationsEnabled', v)}
              disabled={!isOrgOwner}
              last
            />
          ) : (
            <TierLockedToggleRow
              title="Send text notifications"
              featureName="SMS notifications"
              isOrgOwner={isOrgOwner}
              last
            />
          )}
        </div>

        {/* Reminders for unanswered work.
            Two send moments the owner picks, rather than a rolling "every N
            days" timer: crew learn when the nudge lands and admins can put it
            just before the days they actually staff. */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Reminders for Unanswered Work
          </h2>

          <ToggleRow
            title="Remind crew about anything they have not answered"
            description="One email per person, covering the next 7 days: events they have not set availability on, plus assignments they have not confirmed or declined. Nothing is sent to anyone with nothing outstanding."
            enabled={formData.pendingNudgeEnabled}
            onChange={(v) => {
              // On with no send times would never send (the server applies
              // the same default): give the owner a slot to edit, not a
              // toggle that reads as working.
              setFormData((prev) => ({
                ...prev,
                pendingNudgeEnabled: v,
                pendingNudgeSlots:
                  v && prev.pendingNudgeSlots.length === 0
                    ? [{ day: 'Mon', hour: 9 }]
                    : prev.pendingNudgeSlots,
              }));
            }}
            disabled={!isOrgOwner}
            last
          />

          <div className={`pt-4 ${formData.pendingNudgeEnabled ? '' : 'opacity-50'}`}>
            <p className="block text-sm font-medium text-gray-700 mb-2">
              Send times (up to {MAX_NUDGE_SLOTS})
            </p>

            {formData.pendingNudgeSlots.length === 0 ? (
              <p className="text-sm text-gray-500 mb-3">
                No send times set, so no reminders go out.
              </p>
            ) : (
              <div className="space-y-2 mb-3">
                {formData.pendingNudgeSlots.map((slot, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <select
                      aria-label={`Reminder ${index + 1} day`}
                      value={slot.day}
                      disabled={!isOrgOwner || !formData.pendingNudgeEnabled}
                      onChange={(e) =>
                        updateNudgeSlot(index, { day: e.target.value as DigestWeekday })
                      }
                      className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {WEEKDAY_OPTIONS.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-500">at</span>
                    <select
                      aria-label={`Reminder ${index + 1} time`}
                      value={slot.hour}
                      disabled={!isOrgOwner || !formData.pendingNudgeEnabled}
                      onChange={(e) =>
                        updateNudgeSlot(index, { hour: Number(e.target.value) })
                      }
                      className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {HOUR_OPTIONS.map((h) => (
                        <option key={h.value} value={h.value}>
                          {h.label}
                        </option>
                      ))}
                    </select>
                    {isOrgOwner && (
                      <button
                        type="button"
                        onClick={() => removeNudgeSlot(index)}
                        disabled={!formData.pendingNudgeEnabled}
                        className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {isOrgOwner && formData.pendingNudgeSlots.length < MAX_NUDGE_SLOTS && (
              <button
                type="button"
                onClick={addNudgeSlot}
                disabled={!formData.pendingNudgeEnabled}
                className="text-sm font-medium text-rack hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                + Add a send time
              </button>
            )}

            <p className="text-xs text-gray-500 mt-3">
              Times are in {organization?.timezone || 'the organization timezone'}. Archived
              crew are not reminded; answering anything puts someone back on the roster
              automatically.
            </p>
          </div>
        </div>

        {/* Auto-archive.
            Lives on this page because it is an automation that changes who
            gets notified, which is what everything else here does. What it
            does NOT do is take anything away: archived crew keep their
            accounts and can still respond, and responding un-archives them.
            The copy has to carry that, or an owner reads "archive" as
            "remove" and never turns it on. */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Inactive Crew</h2>

          <ToggleRow
            title="Automatically archive crew who stop responding"
            description="Archived crew come off your roster, out of event notices and out of the assignment picker. They keep their accounts, can still sign in and set availability, and responding to anything puts them straight back on the roster. All of their history stays in your reports. Admins and owners are never archived."
            enabled={formData.autoArchiveEnabled}
            onChange={(v) => setField('autoArchiveEnabled', v)}
            disabled={!isOrgOwner}
            last
          />

          <div className={`pt-4 ${formData.autoArchiveEnabled ? '' : 'opacity-50'}`}>
            <label
              htmlFor="autoArchiveAfterEvents"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Archive after no response to
            </label>
            <select
              id="autoArchiveAfterEvents"
              value={formData.autoArchiveAfterEvents}
              disabled={!isOrgOwner || !formData.autoArchiveEnabled}
              onChange={(e) => setField('autoArchiveAfterEvents', Number(e.target.value))}
              className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {AUTO_ARCHIVE_EVENT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} events
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">
              Counts your last {formData.autoArchiveAfterEvents} events that have already happened.
              A crew member is counted as responding if they set their availability, or confirmed or
              declined an assignment. Anyone who joined after those events started is never archived,
              and owners and admins get an email listing whoever the rule archives.
            </p>
          </div>
        </div>

        {/* Admins digest.
            Replaces the 2026-09-10 "Alerts for Admins" (which only fired when
            a notification was owed). Owners/admins only, plus a curated
            external list; crew never see this email, which is why drafts and
            names are in it. The send buttons only enqueue; the worker sends. */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Admins Digest</h2>
          <p className="text-sm text-gray-500 mb-4">
            A daily email for owners and admins. Anything that still needs action comes first:
            crew who were assigned but have not been sent their notification yet, with a link
            to send them all at once. Then a staffing brief for every event in the next 7 days,
            drafts included: each role, who is on it, whether they have confirmed, whether they
            have been notified, and what is still unfilled. Nothing is sent on a day with no
            events and nothing owed.
          </p>

          <ToggleRow
            title="Send owners and admins a daily digest"
            description="One email at each send time to every owner and admin who has not muted it, plus the additional recipients below."
            enabled={formData.adminDigestEnabled}
            onChange={(v) => {
              // On with no send times would never send (the server applies
              // the same default): give the owner an hour to edit, not a
              // toggle that reads as working.
              setFormData((prev) => ({
                ...prev,
                adminDigestEnabled: v,
                adminDigestHours:
                  v && prev.adminDigestHours.length === 0 ? [8] : prev.adminDigestHours,
              }));
            }}
            disabled={!isOrgOwner}
            last
          />

          <div className={`pt-4 ${formData.adminDigestEnabled ? '' : 'opacity-50'}`}>
            <p className="block text-sm font-medium text-gray-700 mb-2">
              Send times (up to {MAX_ADMIN_DIGEST_HOURS} a day)
            </p>

            {formData.adminDigestHours.length === 0 ? (
              <p className="text-sm text-gray-500 mb-3">
                No send times set, so no digest goes out.
              </p>
            ) : (
              <div className="space-y-2 mb-3">
                {formData.adminDigestHours.map((hour, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Every day at</span>
                    <select
                      aria-label={`Admins digest send time ${index + 1}`}
                      value={hour}
                      disabled={!isOrgOwner || !formData.adminDigestEnabled}
                      onChange={(e) => updateAdminDigestHour(index, Number(e.target.value))}
                      className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {HOUR_OPTIONS.map((h) => (
                        <option key={h.value} value={h.value}>
                          {h.label}
                        </option>
                      ))}
                    </select>
                    {isOrgOwner && (
                      <button
                        type="button"
                        onClick={() => removeAdminDigestHour(index)}
                        disabled={!formData.adminDigestEnabled}
                        className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {isOrgOwner && formData.adminDigestHours.length < MAX_ADMIN_DIGEST_HOURS && (
              <button
                type="button"
                aria-label="Add an admins digest send time"
                onClick={addAdminDigestHour}
                disabled={!formData.adminDigestEnabled}
                className="text-sm font-medium text-rack hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                + Add a send time
              </button>
            )}

            <p className="text-xs text-gray-500 mt-3">
              Times are in {organization?.timezone || 'the organization timezone'}. Owners and admins can
              mute this under Admins digest in their own notification preferences.
            </p>
          </div>

          <div
            className={`mt-4 pt-4 border-t border-gray-100 ${
              formData.adminDigestEnabled ? '' : 'opacity-50'
            }`}
          >
            <ExternalRecipientsEditor
              recipients={formData.adminDigestExternalRecipients}
              onChange={(next) => setField('adminDigestExternalRecipients', next)}
              disabled={!formData.adminDigestEnabled}
              canEdit={isOrgOwner}
              ariaPrefix="Admins digest recipient"
              description="People who are not on ProgramRow can get the event briefs too. They receive the briefs only, including crew names and status, without the action list or any links into the app. Each email includes an unsubscribe link; anyone who unsubscribes, or whose address bounces, is removed from this list automatically, and admins are notified."
            />
          </div>

          {isOrgAdmin && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">Send this email</h3>
              <p className="text-sm text-gray-500 mt-1 mb-3">
                Sends today's digest right now using the saved settings. The scheduled send
                times are not affected.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void triggerAdminDigestSend('preview')}
                  disabled={isDirty || sendingMode !== null}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingMode === 'preview' ? 'Sending...' : 'Send me a preview'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmSendAllOpen(true)}
                  disabled={isDirty || sendingMode !== null}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingMode === 'everyone' ? 'Sending...' : 'Send to everyone now'}
                </button>
              </div>
              {isDirty && (
                <p className="text-xs text-gray-500 mt-2">
                  Save changes first: sending uses the saved settings.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Weekly digest emails */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Weekly Digest Emails</h2>
          <p className="text-sm text-gray-500 mb-4">
            Weekly schedule overviews for the whole team. The crew digest goes to each crew
            member with assignments that week; the org-wide digest goes to everyone in the
            organization, assigned or not. For the daily owners-and-admins email with staffing
            and notification status, see Admins Digest above.
          </p>

          <ToggleRow
            title="Send a weekly schedule email"
            description={`Each week (${
              organization?.timezone || 'organization time'
            }), email each crew member a summary of their assignments for the week ahead. Only crew with upcoming assignments receive it.`}
            enabled={formData.weeklyDigestEnabled}
            onChange={(v) => setField('weeklyDigestEnabled', v)}
            disabled={!isOrgOwner}
            last
          />

          <div
            className={`pt-4 flex flex-wrap items-end gap-2 ${
              formData.weeklyDigestEnabled ? '' : 'opacity-50'
            }`}
          >
            <div>
              <label
                htmlFor="weeklyDigestDay"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Send every
              </label>
              <select
                id="weeklyDigestDay"
                aria-label="Schedule digest day"
                value={formData.weeklyDigestDay}
                disabled={!isOrgOwner || !formData.weeklyDigestEnabled}
                onChange={(e) => setField('weeklyDigestDay', e.target.value as DigestWeekday)}
                className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {WEEKDAY_OPTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="weeklyDigestHour"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                at
              </label>
              <select
                id="weeklyDigestHour"
                aria-label="Schedule digest time"
                value={formData.weeklyDigestHour}
                disabled={!isOrgOwner || !formData.weeklyDigestEnabled}
                onChange={(e) => setField('weeklyDigestHour', Number(e.target.value))}
                className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {HOUR_OPTIONS.map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t border-gray-100 mt-6 pt-2">
            <ToggleRow
              title="Send an org-wide weekly digest"
              description="Email everyone in the organization a schedule of all events in the next 7 days, even members without assignments. Members can opt out via the Weekly digest row in their notification preferences."
              enabled={formData.orgWeeklyDigestEnabled}
              onChange={(v) => setField('orgWeeklyDigestEnabled', v)}
              disabled={!isOrgOwner}
              last
            />

            <div
              className={`pt-4 flex flex-wrap items-end gap-2 ${
                formData.orgWeeklyDigestEnabled ? '' : 'opacity-50'
              }`}
            >
              <div>
                <label
                  htmlFor="orgWeeklyDigestDay"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Send every
                </label>
                <select
                  id="orgWeeklyDigestDay"
                  aria-label="Org-wide digest day"
                  value={formData.orgWeeklyDigestDay}
                  disabled={!isOrgOwner || !formData.orgWeeklyDigestEnabled}
                  onChange={(e) =>
                    setField('orgWeeklyDigestDay', e.target.value as DigestWeekday)
                  }
                  className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {WEEKDAY_OPTIONS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="orgWeeklyDigestHour"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  at
                </label>
                <select
                  id="orgWeeklyDigestHour"
                  aria-label="Org-wide digest time"
                  value={formData.orgWeeklyDigestHour}
                  disabled={!isOrgOwner || !formData.orgWeeklyDigestEnabled}
                  onChange={(e) => setField('orgWeeklyDigestHour', Number(e.target.value))}
                  className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {HOUR_OPTIONS.map((h) => (
                    <option key={h.value} value={h.value}>
                      {h.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Admins and owners receive a draft preview 24 hours before this time, so events
              can be corrected before the digest goes out.
            </p>

            <div
              className={`mt-4 pt-4 border-t border-gray-100 ${
                formData.orgWeeklyDigestEnabled ? '' : 'opacity-50'
              }`}
            >
              <ExternalRecipientsEditor
                recipients={formData.digestExternalRecipients}
                onChange={(next) => setField('digestExternalRecipients', next)}
                disabled={!formData.orgWeeklyDigestEnabled}
                canEdit={isOrgOwner}
                description="People who aren't on ProgramRow can get the org-wide digest too. Each email includes an unsubscribe link; anyone who unsubscribes, or whose address bounces, is removed from this list automatically, and admins are notified."
              />
            </div>
          </div>
        </div>

        {/* Default reminders for new crew */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            Default Reminders for New Crew
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Start new crew members with these event reminders. Applies to crew you add from
            now on; existing members keep their own settings, and everyone can change
            theirs anytime.
          </p>

          {formData.defaultCrewReminders.length === 0 && (
            <p className="text-sm text-gray-500 mb-3">No default reminders set.</p>
          )}

          <div className="space-y-2">
            {formData.defaultCrewReminders.map((reminder, index) => (
              <div key={index} className="flex flex-wrap items-center gap-2">
                <select
                  aria-label={`Default reminder ${index + 1} method`}
                  value={reminder.method}
                  disabled={!isOrgOwner}
                  onChange={(e) =>
                    updateReminderRow(index, {
                      method: e.target.value as EventReminder['method'],
                    })
                  }
                  className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack disabled:bg-gray-100"
                >
                  {METHOD_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>

                <select
                  aria-label={`Default reminder ${index + 1} timing`}
                  value={reminder.timing}
                  disabled={!isOrgOwner}
                  onChange={(e) =>
                    updateReminderRow(index, { timing: e.target.value as ReminderTiming })
                  }
                  className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack disabled:bg-gray-100"
                >
                  {TIMING_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>

                <span className="text-sm text-gray-500">before crew call</span>

                {isOrgOwner && (
                  <button
                    type="button"
                    aria-label={`Remove default reminder ${index + 1}`}
                    onClick={() => removeReminderRow(index)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-50"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {isOrgOwner && formData.defaultCrewReminders.length < MAX_DEFAULT_REMINDERS && (
            <button
              type="button"
              onClick={addReminderRow}
              className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              + Add reminder
            </button>
          )}
        </div>

        {/* Save Button */}
        {isOrgOwner && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-r-transparent mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        )}
      </form>

      <ConfirmDialog
        isOpen={confirmSendAllOpen}
        onClose={() => setConfirmSendAllOpen(false)}
        onConfirm={() => void triggerAdminDigestSend('everyone')}
        title="Send the admins digest now?"
        message="This sends today's digest to every owner and admin who has not muted it, plus the additional recipients on the saved list. The scheduled send times are not affected."
        confirmLabel="Send now"
        cancelLabel="Cancel"
        variant="default"
        isLoading={sendingMode === 'everyone'}
      />
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

// =============================================================================
// Icons
// =============================================================================

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

